// Bramka PIN-u rodzica (patch-34): okienko, które przed czynnością „dla dorosłych” (wylogowanie urządzenia) prosi o PIN rodzica
// i sprawdza go na serwerze (ten sam klient i te same blokady co panel rodzica). Dziecko nie wyloguje urządzenia przypadkiem.
// Napisy okienka idą za przełącznikiem EN/PL; komunikaty z serwera zostają po polsku, jak w całym panelu rodzica.
import { createAdminClient } from '../lib/adminApi.ts';
import type { AdminClient } from '../lib/adminApi.ts';
import type { Pair } from '../lib/i18n.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { liveStatus } from './i18nLive.ts';
import { txAttr, txText } from './i18n.ts';
import { icon } from './kit.ts';

const T = {
  pin: { en: 'Parent PIN', pl: 'PIN rodzica' },
  cancel: { en: 'Cancel', pl: 'Anuluj' },
  needPin: { en: 'Enter the parent PIN.', pl: 'Wpisz PIN rodzica.' },
  checking: { en: 'Checking…', pl: 'Sprawdzam…' },
} as const satisfies Record<string, Pair>;

export interface ParentGateOptions {
  title: Pair;
  note?: Pair;
  /** Napis na przycisku potwierdzenia. */
  action: Pair;
  /** Wywoływane po poprawnym PIN-ie (okienko jest już zamknięte). */
  onOk(): void;
  createClient?: (pin: string) => AdminClient;
}

export function openParentGate(ctx: AppCtx, opts: ParentGateOptions): { el: HTMLElement; close(): void } {
  const make = opts.createClient ?? ((pin: string) => createAdminClient(pin));
  let busy = false;
  const pin = txAttr(ctx, txAttr(ctx, h('input', { type: 'password', autocomplete: 'off', 'data-testid': 'gate-pin' }), 'placeholder', T.pin), 'aria-label', T.pin);
  const message = h('p', { class: 'pin-message', role: 'status', 'data-testid': 'gate-message' });
  const status = liveStatus(ctx, message);
  const go = txText(ctx, button('', () => void submit(), { class: 'btn primary', testid: 'gate-ok' }), opts.action);
  const close = (): void => el.remove();
  const cancel = txText(ctx, button('', close, { class: 'btn link', testid: 'gate-cancel' }), T.cancel);

  async function submit(): Promise<void> {
    if (busy) return;
    const value = pin.value.trim();
    if (!value) {
      status.show((lang) => T.needPin[lang]);
      return;
    }
    busy = true;
    go.disabled = true;
    status.show((lang) => T.checking[lang]);
    try {
      const r = await make(value).overview(); // PIN rodzica jest dobry, jeśli serwer wpuszcza do panelu
      if (!r.ok) {
        status.show(() => `❌ ${r.message}`);
        return;
      }
      pin.value = '';
      close();
      opts.onOk();
    } finally {
      busy = false;
      go.disabled = false;
    }
  }
  pin.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') void submit();
  });

  const el = h('div', { class: 'pin-overlay', 'data-testid': 'parent-gate', role: 'dialog', 'aria-modal': 'true' },
    h('div', { class: 'pin-card gate-card' },
      icon('lock', 'gate-lock'),
      txText(ctx, h('h2', { class: 'pin-title' }), opts.title),
      opts.note && txText(ctx, h('p', { class: 'login-note' }), opts.note),
      pin,
      message,
      h('div', { class: 'row gate-actions' }, go, cancel),
    ),
  );
  txAttr(ctx, el, 'aria-label', opts.title);
  ctx.root.append(el);
  return { el, close };
}
